import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { IGridConfig } from '../../../types/grid';
import { useTranslation } from 'react-i18next';
import {
  getBuList,
  getCompanyType,
  getLoginBUName,
} from '../../../utils/jwt-decode';
import { useNavigate } from 'react-router-dom';
import Grid from '../../common/grid/Grid';
import { Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/excel_exporter';
import saveAs from 'file-saver';
import dxDataGrid from 'devextreme/ui/data_grid';
import { InitializedEventInfo } from 'devextreme/events';
import { MarsApiConfig as apiConfig } from '../../../api/mars-api-config';
import { MarsApiService as api } from '../../../api/mars-api-service';
import { useLoaderContext } from '../../../contexts/LoaderContext';
import { useDateFormat } from '../../../hooks/useDateFormat';

function ComplianceListGrid(
  props: Readonly<{
    imoNumber?: string;
    isFromComplianceHistory?: boolean;
  }>,
  ref?: any
) {
  const { isFromComplianceHistory, imoNumber } = props;
  const { t } = useTranslation();
  const [complianceCheckList, setComplianceCheckList] = useState<any>();
  const companyType = getCompanyType();
  const navigate = useNavigate();
  const { setIsLoading } = useLoaderContext();

  const [gridInstance, setGridInstance] = useState<dxDataGrid<any, any>>();
  const bu: any = getBuList();
  const buList = JSON.parse(bu);
  const currentlyLoggedInBuu = getLoginBUName();
  const filteredBU = buList
    ? buList.find((bu: any) => bu.buName == currentlyLoggedInBuu)
    : 0;
  const { dateFormat } = useDateFormat();

  const onGridInitialized = (e: InitializedEventInfo<dxDataGrid<any, any>>) => {
    setGridInstance(e.component);
  };

  useEffect(() => {
    getComplianceList();
  }, []);

  const getComplianceList = async () => {
    await api
      .get(
        {
          url: apiConfig.complianceList,
          params: { imo: imoNumber, buid: filteredBU.buId },
        },
        setIsLoading
      )
      .then((res) => {
        setComplianceCheckList(res);
      });
  };

  const onRowClick = (e: any) => {
    if (e.data) {
      navigate('/compliance-check', {
        state: {
          imoNumber: e.data.imoNumber,
          complianceCheckId: e.data.complianceCheckId,
          vetRequestId: e.data.vetRequestId,
        },
      });
    }
  };

  const exportComplianceHistoryGrid = () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: gridInstance,
      worksheet: worksheet,
    }).then(function () {
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          'ComplianceList.xlsx'
        );
      });
    });
  };

  useImperativeHandle(ref, () => ({
    exportComplianceHistoryGrid,
  }));

  const gridConfig: IGridConfig = {
    dataSource: complianceCheckList,
    initialized: onGridInitialized,
    remoteOperations: true,
    defaultColumns: [
      {
        caption: t('labels.complianceCheckId'),
        dataField: 'complianceCheckId',
        dataType: 'string',
        minWidth: 150,
      },
      {
        caption: t('labels.vesselName'),
        dataField: 'vesselName',
        dataType: 'string',
        minWidth: 150,
        visible: !isFromComplianceHistory,
      },
      {
        caption: t('labels.IMO'),
        dataField: 'imoNumber',
        dataType: 'string',
        minWidth: 150,
        visible: !isFromComplianceHistory,
      },
      {
        caption: t('labels.company'),
        dataField: 'companyName',
        dataType: 'string',
        minWidth: 150,
        visible: companyType == 'M',
      },
      {
        caption: t('labels.businessUnit'),
        dataField: 'buName',
        dataType: 'string',
        minWidth: 150,
        visible: companyType != 'R',
      },
      {
        caption: t('labels.requestorName'),
        dataField: 'requestedUserName',
        dataType: 'string',
        minWidth: 150,
      },
      {
        caption: t('labels.requestedDate'),
        dataField: 'requestedDateTime',
        dataType: 'date',
        format: dateFormat,
        minWidth: 150,
        sortOrder: 'desc',
      },
      {
        caption: t('labels.vetId'),
        dataField: 'vetRequestId',
        dataType: 'string',
        minWidth: 150,
      },
    ],
    showHeader: true,
    header: !isFromComplianceHistory ? t('labels.complianceCheck') : '',
    hidePagination: false,
    showExport: !isFromComplianceHistory,
    excelFileName: 'ComplianceList.xlsx',
    rowClick: onRowClick,
  };
  return (
    <div
      className={
        isFromComplianceHistory
          ? 'm-c-grid m-c-block-grid-list'
          : 'inner-wrapper page-inner-wrap'
      }
    >
      <Grid gridConfig={gridConfig} />
    </div>
  );
}

export default forwardRef(ComplianceListGrid);
